import React from 'react';
import Footer from '../components/Footer';
import MarketingPublicNav from '../components/MarketingPublicNav';
import { PublicBandSection, PublicMarketingLayout } from '../components/PublicMarketingLayout';

const Terms: React.FC = () => {
  return (
    <PublicMarketingLayout>
      <MarketingPublicNav />

      <PublicBandSection band={1} className="px-6 pb-8 pt-16">
        <div className="mx-auto max-w-4xl">
          <h1 className="mb-4 bg-gradient-to-r from-violet-500 to-blue-500 bg-clip-text text-5xl font-bold text-transparent">
            Terms of Service
          </h1>
          <p className="text-gray-400">Last updated: March 28, 2025</p>
        </div>
      </PublicBandSection>

      <PublicBandSection band={2} className="px-6 pb-16 pt-4">
        <div className="mx-auto max-w-4xl">
        <div className="space-y-12">
          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">1. Acceptance of Terms</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Welcome to kol.my. By accessing and using our platform, you accept and agree to be bound by the terms and conditions of this agreement. If you do not agree to abide by these terms, please do not use this service.
            </p>
            <p className="text-gray-400 leading-relaxed">
              These Terms of Service ("Terms") govern your use of the kol.my platform operated by kol.my Platform Sdn. Bhd. ("kol.my", "we", "us", or "our").
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">2. Description of Service</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              kol.my is an Influencer Marketing AI Platform that connects brands with Key Opinion Leaders (KOLs) and content creators. Our services include:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-400">
              <li>KOL discovery and matching</li>
              <li>Campaign management tools</li>
              <li>Contract and agreement management</li>
              <li>Script collaboration and approval</li>
              <li>Performance analytics and reporting</li>
              <li>Payment processing and milestone tracking</li>
              <li>Messaging and communication tools</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">3. User Accounts</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              To use our platform, you must register for an account. You agree to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-400">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and update your information promptly</li>
              <li>Keep your password secure and confidential</li>
              <li>Notify us immediately of any unauthorized use</li>
              <li>Accept responsibility for all activities under your account</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">4. For Brands</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              As a brand user, you agree to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-400">
              <li>Only post legitimate campaign opportunities</li>
              <li>Pay KOLs promptly per agreed terms</li>
              <li>Not engage in deceptive or misleading marketing</li>
              <li>Respect KOLs' creative freedom and content guidelines</li>
              <li>Comply with all applicable advertising regulations</li>
              <li>Maintain professionalism in all communications</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">5. For KOLs</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              As a KOL user, you agree to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-400">
              <li>Provide accurate profile and audience information</li>
              <li>Deliver content as agreed in campaign specifications</li>
              <li>Maintain authenticity in sponsored content</li>
              <li>Comply with advertising disclosure requirements</li>
              <li>Meet agreed-upon deadlines and milestones</li>
              <li>Only accept campaigns you can genuinely fulfill</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">6. Content Ownership</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              <strong className="text-white">Your Content:</strong> You retain ownership of all content you create and post on the platform. By posting content, you grant kol.my a limited license to use, display, and distribute it for platform purposes.
            </p>
            <p className="text-gray-400 mb-4 leading-relaxed">
              <strong className="text-white">Campaign Content:</strong> Content created for campaigns is subject to the specific agreement between the brand and KOL. We recommend clear contracts outlining usage rights before campaign start.
            </p>
            <p className="text-gray-400 leading-relaxed">
              <strong className="text-white">kol.my Content:</strong> All platform design, logos, and proprietary technology are owned by kol.my and protected by intellectual property laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">7. Fees and Payments</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              <strong className="text-white">Platform Fees:</strong> kol.my charges brands a platform fee for using our services. Fee structures are clearly communicated and may vary based on subscription tier.
            </p>
            <p className="text-gray-400 mb-4 leading-relaxed">
              <strong className="text-white">Payment Processing:</strong> We use secure third-party payment processors. All payment terms agreed between brands and KOLs must be honored.
            </p>
            <p className="text-gray-400 leading-relaxed">
              <strong className="text-white">Refunds:</strong> Refund policies are defined in individual service agreements. Contact support for payment-related disputes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">8. Prohibited Conduct</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              You agree not to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-400">
              <li>Violate any laws, regulations, or third-party rights</li>
              <li>Post false, misleading, or deceptive content</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Upload viruses or malicious code</li>
              <li>Attempt to circumvent our security measures</li>
              <li>Use the platform for unauthorized commercial purposes</li>
              <li>Spam or send unsolicited communications</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">9. Disclaimer of Warranties</h2>
            <p className="text-gray-400 leading-relaxed">
              THE PLATFORM AND SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND. WE DO NOT GUARANTEE THAT THE PLATFORM WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">10. Limitation of Liability</h2>
            <p className="text-gray-400 leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, KOL.MY SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">11. Termination</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              We may terminate or suspend your account immediately, without prior notice, for any breach of these Terms. Upon termination, your right to use the platform ceases immediately.
            </p>
            <p className="text-gray-400 leading-relaxed">
              You may cancel your account at any time by contacting support or using the account deletion feature in settings.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">12. Governing Law</h2>
            <p className="text-gray-400 leading-relaxed">
              These Terms are governed by the laws of Malaysia. Any disputes shall be resolved in the courts of Kuala Lumpur, Malaysia.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">13. Changes to Terms</h2>
            <p className="text-gray-400 leading-relaxed">
              We reserve the right to modify these terms at any time. We will notify users of significant changes via email or platform notifications. Continued use of the platform after changes constitutes acceptance of new terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">14. Contact Information</h2>
            <p className="text-gray-400 leading-relaxed">
              For questions about these Terms, please contact us:<br />
              <strong className="text-white">Email:</strong> i@kol.my<br />
              <strong className="text-white">Address:</strong> Suite 12.03, Level 12, Wisma UOA Pantai, 59200 Kuala Lumpur, Malaysia
            </p>
          </section>
        </div>
        </div>
      </PublicBandSection>

      <Footer />
    </PublicMarketingLayout>
  );
};

export default Terms;
